import { AlertCircle, CalendarPlus, DollarSign, Percent, TrendingUp, Users } from 'lucide-react';
import { Spinner } from '@/components/ui';
import { useDealAnalysis } from '@/hooks/use-deal-analysis';

function fmt(value: number): string {
  return new Intl.NumberFormat('en-AU', {
    style: 'currency',
    currency: 'AUD',
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(value);
}

function fmtExact(value: number): string {
  return new Intl.NumberFormat('en-AU', {
    style: 'currency',
    currency: 'AUD',
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(value);
}

interface Props {
  loanId: string;
}

export function DealAnalysisTab({ loanId }: Props) {
  const { data, isLoading, isError } = useDealAnalysis(loanId);

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-20 text-gray-400">
        <Spinner size="lg" className="w-5 h-5 animate-spin mr-2" />
        Calculating deal analysis…
      </div>
    );
  }

  if (isError || !data) {
    return (
      <div className="flex items-center justify-center py-20 text-red-500">
        <AlertCircle className="w-5 h-5 mr-2" />
        Failed to load deal analysis
      </div>
    );
  }

  const returnColor =
    data.annualisedReturn >= 15
      ? 'text-green-700'
      : data.annualisedReturn >= 8
        ? 'text-amber-700'
        : 'text-red-600';
  const returnBg =
    data.annualisedReturn >= 15
      ? 'bg-green-50 border-green-200'
      : data.annualisedReturn >= 8
        ? 'bg-amber-50 border-amber-200'
        : 'bg-red-50 border-red-200';

  return (
    <div className="space-y-5">
      {/* Return headline cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <div className={`rounded-lg border p-4 ${returnBg}`}>
          <div className="flex items-center gap-2 mb-1">
            <TrendingUp className="w-4 h-4 text-gray-400" />
            <p className="text-xs font-medium text-gray-500">Annualised Return</p>
          </div>
          <p className={`text-2xl font-bold ${returnColor}`}>{data.annualisedReturn.toFixed(2)}%</p>
          <p className="text-[10px] text-gray-400 mt-0.5">per annum on principal</p>
        </div>

        <div className="rounded-lg border border-indigo-200 bg-indigo-50 p-4">
          <div className="flex items-center gap-2 mb-1">
            <Percent className="w-4 h-4 text-gray-400" />
            <p className="text-xs font-medium text-gray-500">Return on Principal</p>
          </div>
          <p className="text-2xl font-bold text-indigo-700">{data.returnOnPrincipal.toFixed(2)}%</p>
          <p className="text-[10px] text-gray-400 mt-0.5">over full {data.termMonths}mo term</p>
        </div>

        <div className="rounded-lg border border-gray-200 bg-white p-4">
          <div className="flex items-center gap-2 mb-1">
            <DollarSign className="w-4 h-4 text-gray-400" />
            <p className="text-xs font-medium text-gray-500">Total Lender Return</p>
          </div>
          <p className="text-2xl font-bold text-gray-900">{fmt(data.totalLenderReturn)}</p>
          <p className="text-[10px] text-gray-400 mt-0.5">fees retained + interest income</p>
        </div>

        <div className="rounded-lg border border-gray-200 bg-white p-4">
          <div className="flex items-center gap-2 mb-1">
            <DollarSign className="w-4 h-4 text-gray-400" />
            <p className="text-xs font-medium text-gray-500">Principal</p>
          </div>
          <p className="text-2xl font-bold text-gray-900">{fmt(data.principal)}</p>
          <p className="text-[10px] text-gray-400 mt-0.5">
            {data.interestRate}% p.a. · {data.termMonths} months
            {data.extensionCount > 0 && (
              <span className="text-blue-500"> (incl. {data.totalAdditionalMonths}mo ext.)</span>
            )}
          </p>
        </div>
      </div>

      {/* Fee Breakdown */}
      <div className="bg-white rounded-lg border border-gray-200 p-5">
        <h3 className="text-sm font-semibold text-gray-900 mb-4">Fee Breakdown</h3>
        <div className="space-y-3">
          <FeeRow
            label="Establishment Fee"
            total={data.totalEstablishmentFee}
            retained={data.establishmentFeeRetained}
          />
          <FeeRow
            label="Brokerage Fee"
            total={data.totalBrokerageFee}
            retained={data.brokerageFeeRetained}
          />
          <FeeRow
            label="Legal Costs"
            total={data.totalLegalCosts}
            retained={data.totalLegalCosts}
          />
          <FeeRow
            label="Other Costs"
            total={data.totalOtherCosts}
            retained={data.totalOtherCosts}
          />
          {data.totalExtensionFees > 0 && (
            <FeeRow
              label={`Extension Fees (×${data.extensionCount})`}
              total={data.totalExtensionFees}
              retained={data.extensionFeesRetained}
            />
          )}

          <div className="border-t border-gray-200 pt-3 mt-3">
            <div className="flex items-center justify-between text-sm">
              <span className="font-semibold text-gray-900">Total Fees Charged</span>
              <span className="font-semibold text-gray-900">{fmtExact(data.totalFeesCharged)}</span>
            </div>
          </div>
        </div>
      </div>

      {/* Lender revenue split */}
      <div className="bg-white rounded-lg border border-gray-200 p-5">
        <h3 className="text-sm font-semibold text-gray-900 mb-4">Lender Revenue Split</h3>

        {/* Visual bar */}
        <div className="mb-4">
          <div className="h-8 rounded-lg overflow-hidden flex border border-gray-200">
            {data.totalLenderRetainedFees > 0 && (
              <div
                className="bg-indigo-500 flex items-center justify-center transition-all"
                style={{
                  width: `${(data.totalLenderRetainedFees / (data.totalLenderRetainedFees + data.totalPaidOut + data.totalInterestIncome || 1)) * 100}%`,
                }}
                title={`Lender Retained Fees: ${fmtExact(data.totalLenderRetainedFees)}`}
              >
                {data.totalLenderRetainedFees > 0 && (
                  <span className="text-[10px] font-semibold text-white truncate px-1">Fees</span>
                )}
              </div>
            )}
            {data.totalInterestIncome > 0 && (
              <div
                className="bg-green-500 flex items-center justify-center transition-all"
                style={{
                  width: `${(data.totalInterestIncome / (data.totalLenderRetainedFees + data.totalPaidOut + data.totalInterestIncome || 1)) * 100}%`,
                }}
                title={`Interest Income: ${fmtExact(data.totalInterestIncome)}`}
              >
                <span className="text-[10px] font-semibold text-white truncate px-1">Interest</span>
              </div>
            )}
            {data.totalPaidOut > 0 && (
              <div
                className="bg-red-400 flex items-center justify-center transition-all"
                style={{
                  width: `${(data.totalPaidOut / (data.totalLenderRetainedFees + data.totalPaidOut + data.totalInterestIncome || 1)) * 100}%`,
                }}
                title={`Paid Out: ${fmtExact(data.totalPaidOut)}`}
              >
                <span className="text-[10px] font-semibold text-white truncate px-1">Paid Out</span>
              </div>
            )}
          </div>
          <div className="flex items-center gap-4 mt-2 text-xs text-gray-500">
            <span className="flex items-center gap-1">
              <span className="w-2.5 h-2.5 rounded-sm bg-indigo-500" />
              Lender Retained Fees: {fmtExact(data.totalLenderRetainedFees)}
            </span>
            <span className="flex items-center gap-1">
              <span className="w-2.5 h-2.5 rounded-sm bg-green-500" />
              Interest Income: {fmtExact(data.totalInterestIncome)}
            </span>
            {data.totalPaidOut > 0 && (
              <span className="flex items-center gap-1">
                <span className="w-2.5 h-2.5 rounded-sm bg-red-400" />
                Referrer/Broker: {fmtExact(data.totalPaidOut)}
              </span>
            )}
          </div>
        </div>

        <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
          <MetricCard
            label="Lender Retained Fees"
            value={fmtExact(data.totalLenderRetainedFees)}
            variant="indigo"
          />
          <MetricCard
            label="Interest Income"
            value={fmtExact(data.totalInterestIncome)}
            variant="green"
          />
          <MetricCard
            label="Referrer Commission"
            value={data.totalPaidOut > 0 ? fmtExact(data.totalPaidOut) : '—'}
            variant={data.totalPaidOut > 0 ? 'red' : 'gray'}
          />
        </div>
      </div>

      {/* Extension Banner */}
      {data.extensionCount > 0 && (
        <div className="bg-blue-50 rounded-lg border border-blue-200 p-4">
          <div className="flex items-center gap-2 mb-2">
            <CalendarPlus className="w-4 h-4 text-blue-600" />
            <h3 className="text-sm font-semibold text-blue-900">
              Term Extended {data.extensionCount} time{data.extensionCount !== 1 ? 's' : ''}
            </h3>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
            <div>
              <span className="text-xs text-blue-500">Original Term</span>
              <p className="font-medium text-blue-900">{data.originalTermMonths} months</p>
            </div>
            <div>
              <span className="text-xs text-blue-500">Added</span>
              <p className="font-medium text-blue-900">+{data.totalAdditionalMonths} months</p>
            </div>
            <div>
              <span className="text-xs text-blue-500">Current Term</span>
              <p className="font-semibold text-blue-900">{data.termMonths} months</p>
            </div>
            <div>
              <span className="text-xs text-blue-500">Total Extension Fees</span>
              <p className="font-medium text-blue-900">{fmtExact(data.totalExtensionFees)}</p>
            </div>
          </div>
        </div>
      )}

      {/* Referrer Info */}
      {data.referrer && (
        <div className="bg-white rounded-lg border border-gray-200 p-5">
          <div className="flex items-center gap-2 mb-3">
            <Users className="w-4 h-4 text-gray-400" />
            <h3 className="text-sm font-semibold text-gray-900">Referrer</h3>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
            <div>
              <span className="text-xs text-gray-500">Name</span>
              <p className="font-medium text-gray-900">{data.referrer.name}</p>
            </div>
            {data.referrer.company && (
              <div>
                <span className="text-xs text-gray-500">Company</span>
                <p className="font-medium text-gray-900">{data.referrer.company}</p>
              </div>
            )}
            {data.referrer.commissionRate !== undefined && (
              <div>
                <span className="text-xs text-gray-500">Commission Rate</span>
                <p className="font-medium text-gray-900">{data.referrer.commissionRate}%</p>
              </div>
            )}
            <div>
              <span className="text-xs text-gray-500">Commission Earned</span>
              <p className="font-medium text-red-600">{fmtExact(data.totalPaidOut)}</p>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function FeeRow({ label, total, retained }: { label: string; total: number; retained: number }) {
  const paidOut = total - retained;

  if (total === 0) return null;

  return (
    <div className="flex items-center justify-between text-sm">
      <span className="text-gray-600">{label}</span>
      <div className="flex items-center gap-6">
        <span className="text-gray-400 text-xs w-24 text-right">Total: {fmtExact(total)}</span>
        <span className="text-indigo-700 font-medium w-28 text-right">
          Lender: {fmtExact(retained)}
        </span>
        {paidOut > 0 && (
          <span className="text-red-500 text-xs w-28 text-right">
            Paid out: {fmtExact(paidOut)}
          </span>
        )}
      </div>
    </div>
  );
}

function MetricCard({
  label,
  value,
  variant,
}: {
  label: string;
  value: string;
  variant: 'indigo' | 'green' | 'red' | 'gray';
}) {
  const styles = {
    indigo: 'border-indigo-200 bg-indigo-50 text-indigo-700',
    green: 'border-green-200 bg-green-50 text-green-700',
    red: 'border-red-200 bg-red-50 text-red-700',
    gray: 'border-gray-200 bg-gray-50 text-gray-500',
  };

  return (
    <div className={`rounded-lg border p-3 ${styles[variant]}`}>
      <p className="text-[10px] font-medium opacity-70 mb-0.5">{label}</p>
      <p className="text-lg font-semibold">{value}</p>
    </div>
  );
}
